import { AssessmentVideoTitleFields } from "./types/assessmentVideoTitleFields";
import {
  RebaScoreCSV,
  RulaScoreCSV,
} from "src/services/assessment/assessmentService";
import {
  REBA_SCORE_OK_RANGE_TEXT,
  REBA_SCORE_BAD_RANGE_TEXT,
  REBA_SCORE_EXCELLENT_RANGE_TEXT,
  REBA_SCORE_VERY_BAD_RANGE_TEXT,
  REBA_SCORE_WORST_RANGE_TEXT,
} from "src/technicalRiskAssessment/components/pages/assessmentLibrary/constants";
import {
  REBA_SCORE_WORST_CSS_BORDER_CLASS,
  REBA_SCORE_VERY_BAD_CSS_BORDER_CLASS,
  REBA_SCORE_BAD_CSS_BORDER_CLASS,
  REBA_SCORE_OK_CSS_BORDER_CLASS,
  REBA_SCORE_EXCELLENT_CSS_BORDER_CLASS,
} from "./constants";
import { EvaluationStatusEnum } from "src/services/assessment/types/evaluationStatusEnum";
import { getTimeDiffInSeconds } from "src/utils/dateUtil";
// Common operations related to Assessments across
// different UI pages

const findMaxRebaScoreIndices = (data: RebaScoreCSV[]): number[] => {
  // Filter out nan rebascores and find max score
  const notNaNRebaScores = data
    .filter(({ rebaScore }) => {
      return !isNaN(parseFloat(rebaScore));
    })
    .map(({ rebaScore }) => {
      return parseFloat(rebaScore);
    });

  const maxScore = Math.max(...notNaNRebaScores);
  return data.reduce((indexes: number[], { rebaScore }, i) => {
    if (parseFloat(rebaScore) === maxScore) {
      indexes.push(i);
    }
    return indexes;
  }, []);
};
const findMaxRulaScoreIndices = (data: RulaScoreCSV[]): number[] => {
  // Filter out nan rebascores and find max score
  const notNaNRulaScores = data
    .filter(({ rulaScore }) => {
      return !isNaN(parseFloat(rulaScore));
    })
    .map(({ rulaScore }) => {
      return parseFloat(rulaScore);
    });

  const maxScore = Math.max(...notNaNRulaScores);
  return data.reduce((indexes: number[], { rulaScore }, i) => {
    if (parseFloat(rulaScore) === maxScore) {
      indexes.push(i);
    }
    return indexes;
  }, []);
};
const findMaxRebaScoreDurationIdx = (maxIndexes: number[]): number => {
  let longestStartIndex = -1;
  let longestSeriesLength = 0;

  for (const startIndex of maxIndexes) {
    let seriesLength = 1;

    while (maxIndexes.includes(startIndex + seriesLength)) {
      seriesLength++;
    }

    if (seriesLength > longestSeriesLength) {
      [longestSeriesLength, longestStartIndex] = [seriesLength, startIndex];
    }
  }

  return longestStartIndex;
};

class AssessmentUtilService {
  generateVideoTitleFromAssessment(
    assessment: AssessmentVideoTitleFields,
    isMobile?: boolean
  ): string {
    const videoTitle = isMobile
      ? `${assessment.site}-${
          assessment.processPath.length > 4
            ? assessment.processPath.slice(0, 4)
            : assessment.processPath
        }...`
      : `${assessment.site}-${assessment.processPath}-${assessment.activity}`;
    return videoTitle;
  }

  // Returns the css class to be applied depending on the reba score
  // These values are defined in rebaScores.scss
  getRebaBorderClass(score: number): string {
    let borderClass = "";
    if (score >= 0 && score < 3) {
      borderClass = REBA_SCORE_EXCELLENT_CSS_BORDER_CLASS;
    } else if (score >= 3 && score < 5) {
      borderClass = REBA_SCORE_OK_CSS_BORDER_CLASS;
    } else if (score >= 5 && score < 7) {
      borderClass = REBA_SCORE_BAD_CSS_BORDER_CLASS;
    } else if (score >= 7 && score < 11) {
      borderClass = REBA_SCORE_VERY_BAD_CSS_BORDER_CLASS;
    } else if (score >= 11) {
      borderClass = REBA_SCORE_WORST_CSS_BORDER_CLASS;
    }

    return borderClass;
  }

  getRebaBorderClassFromRangeString(rangeString: string) {
    const rebaScoreBorderMap = new Map();
    rebaScoreBorderMap.set(
      REBA_SCORE_WORST_RANGE_TEXT,
      REBA_SCORE_WORST_CSS_BORDER_CLASS
    );
    rebaScoreBorderMap.set(
      REBA_SCORE_VERY_BAD_RANGE_TEXT,
      REBA_SCORE_VERY_BAD_CSS_BORDER_CLASS
    );
    rebaScoreBorderMap.set(
      REBA_SCORE_BAD_RANGE_TEXT,
      REBA_SCORE_BAD_CSS_BORDER_CLASS
    );
    rebaScoreBorderMap.set(
      REBA_SCORE_OK_RANGE_TEXT,
      REBA_SCORE_OK_CSS_BORDER_CLASS
    );
    rebaScoreBorderMap.set(
      REBA_SCORE_EXCELLENT_RANGE_TEXT,
      REBA_SCORE_EXCELLENT_CSS_BORDER_CLASS
    );

    return rebaScoreBorderMap.has(rangeString)
      ? rebaScoreBorderMap.get(rangeString)
      : "";
  }

  findHighestRebaScore(rebaScores: RebaScoreCSV[]): RebaScoreCSV {
    if (rebaScores.length === 0) {
      return {
        timeStamp: "",
        rebaScore: "",
        neckScore: "",
        trunkScore: "",
        legScore: "",
        upperArmScore: "",
        lowerArmScore: "",
        wristScore: "",
        loading: "",
        coupling: "",
      };
    }
    const maxScoreIndices: number[] = findMaxRebaScoreIndices(rebaScores);
    const maxDurationIndex: number =
      findMaxRebaScoreDurationIdx(maxScoreIndices);
    const maxDurationScoreObject: RebaScoreCSV = rebaScores[maxDurationIndex];

    return maxDurationScoreObject;
  }
  findHighestRulaScore(rulaScores: RulaScoreCSV[]): RulaScoreCSV {
    if (rulaScores.length === 0) {
      return {
        timeStamp: "",
        rulaScore: "",
        neckScore: "",
        trunkScore: "",
        legScore: "",
        upperArmScore: "",
        lowerArmScore: "",
        wristScore: "",
      };
    }
    const maxScoreIndices: number[] = findMaxRulaScoreIndices(rulaScores);
    const maxDurationIndex: number =
      findMaxRebaScoreDurationIdx(maxScoreIndices);
    const maxDurationScoreObject: RulaScoreCSV = rulaScores[maxDurationIndex];

    return maxDurationScoreObject;
  }

  isUploadOrProcessStatus(status: string) {
    return (
      status === EvaluationStatusEnum.IN_PROGRESS ||
      status === EvaluationStatusEnum.UPLOADING
    );
  }

  isUploadError(status: string, timestamp: number) {
    return (
      status === EvaluationStatusEnum.UPLOADING &&
      getTimeDiffInSeconds(timestamp) > 600
    );
  }
}

export default new AssessmentUtilService();
